import DarkModeRounded from '@mui/icons-material/DarkModeRounded';
import KeyboardDoubleArrowLeftRounded from '@mui/icons-material/KeyboardDoubleArrowLeftRounded';
import LightModeRounded from '@mui/icons-material/LightModeRounded';
import LogoutRounded from '@mui/icons-material/LogoutRounded';
import MenuRounded from '@mui/icons-material/MenuRounded';
import NotificationsNoneRounded from '@mui/icons-material/NotificationsNoneRounded';
import PersonOutlineRounded from '@mui/icons-material/PersonOutlineRounded';
import SearchRounded from '@mui/icons-material/SearchRounded';
import {
  alpha,
  Avatar,
  Badge,
  Box,
  ButtonBase,
  Chip,
  Divider,
  Drawer,
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
  useColorScheme,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router';
import { api, unwrap } from '../api/client';
import { useAuth } from '../auth/AuthProvider';
import { ImageEditor } from '../components/images';
import { useImageSrc } from '../lib/images';
import { initials } from '../lib/format';
import { useShop } from '../shop/ShopProvider';
import { BackButton } from './BackButton';
import { CommandPalette } from './CommandPalette';
import { useSupportUnread } from '../features/support/api';
import { hasScreen } from '../features';
import { FoodpandaAlerts } from '../features/foodpanda/FoodpandaAlerts';
import { useNavFlags } from '../features/pos/navFlags';
import { MenuGuard } from './MenuGuard';
import { FULL_SCREEN_PATHS, visibleNav } from './nav';
import { PoweredBy } from '../components/PoweredBy';
import { LockedScreen, SubscriptionBanner, useSubscriptionGate } from './SubscriptionGate';

const SIDEBAR = 264;
const SIDEBAR_COLLAPSED = 76;

export function AppShell() {
  const theme = useTheme();
  const desktop = useMediaQuery(theme.breakpoints.up('lg'));
  const [collapsed, setCollapsed] = useState(false);
  const [paletteOpen, setPaletteOpen] = useState(false);
  const location = useLocation();
  // The mobile drawer belongs to the page it was opened on, so navigating closes it.
  const [mobileOpenOn, setMobileOpenOn] = useState<string | null>(null);
  const mobileOpen = mobileOpenOn === location.pathname;
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        setPaletteOpen((o) => !o);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  const gate = useSubscriptionGate();

  const width = desktop && collapsed ? SIDEBAR_COLLAPSED : SIDEBAR;
  const sidebar = (
    <Sidebar
      collapsed={desktop && collapsed}
      onToggle={() => setCollapsed((c) => !c)}
      showToggle={desktop}
    />
  );

  if (gate.locked && gate.notice) return <LockedScreen notice={gate.notice} />;

  return (
    <Box sx={{ display: 'flex', minHeight: '100dvh', bgcolor: 'background.default' }}>
      <FoodpandaAlerts />
      {desktop ? (
        <Box
          component="nav"
          sx={{
            width,
            flexShrink: 0,
            transition: theme.transitions.create('width', { duration: 220 }),
            position: 'sticky',
            top: 0,
            height: '100dvh',
          }}
        >
          {sidebar}
        </Box>
      ) : (
        <Drawer
          open={mobileOpen}
          onClose={() => setMobileOpenOn(null)}
          slotProps={{ paper: { sx: { width: SIDEBAR, border: 0 } } }}
        >
          {sidebar}
        </Drawer>
      )}
      <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
        <TopBar
          onMenu={() => setMobileOpenOn(location.pathname)}
          showMenu={!desktop}
          onSearch={() => setPaletteOpen(true)}
        />
        <Box
          component="main"
          key={location.pathname}
          sx={{
            flex: 1,
            px: { xs: 2, sm: 3, lg: 4 },
            py: { xs: 2.5, sm: 3.5 },
            maxWidth: 1480,
            width: '100%',
            mx: 'auto',
            animation: 'sm-page-in 220ms cubic-bezier(0.2,0,0,1)',
            '@keyframes sm-page-in': {
              from: { opacity: 0, transform: 'translateY(4px)' },
              to: { opacity: 1, transform: 'none' },
            },
          }}
        >
          {gate.notice && <SubscriptionBanner notice={gate.notice} />}
          <MenuGuard />
        </Box>
      </Box>
      <CommandPalette open={paletteOpen} onClose={() => setPaletteOpen(false)} />
    </Box>
  );
}

function Sidebar({
  collapsed,
  onToggle,
  showToggle,
}: {
  collapsed: boolean;
  onToggle: () => void;
  showToggle: boolean;
}) {
  const { can, me } = useAuth();
  const { settings } = useShop();
  const groups = visibleNav((k) => can(k), useNavFlags());
  const { data: supportUnread } = useSupportUnread();
  const shopName = settings?.name ?? me?.tenant.name ?? 'Your shop';

  return (
    <Box
      sx={(t) => ({
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.paper',
        borderRight: `1px solid ${t.vars?.palette.divider ?? t.palette.divider}`,
      })}
    >
      <Stack
        direction="row"
        spacing={1.5}
        sx={{ alignItems: 'center', px: collapsed ? 2.25 : 2.5, height: 68, flexShrink: 0 }}
      >
        <BrandMark logoUrl={settings?.logoUrl} />
        {!collapsed && (
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography
              variant="subtitle1"
              noWrap
              sx={{ fontFamily: 'var(--sm-display)', fontWeight: 700, lineHeight: 1.2 }}
            >
              {shopName}
            </Typography>
            <Typography variant="caption" color="text.secondary" noWrap component="div">
              {me?.tenant.slug}
            </Typography>
          </Box>
        )}
        {showToggle && !collapsed && (
          <Tooltip title="Collapse sidebar">
            <IconButton size="small" onClick={onToggle} sx={{ color: 'text.secondary' }}>
              <KeyboardDoubleArrowLeftRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
      </Stack>

      <Box sx={{ flex: 1, overflowY: 'auto', px: 1.5, pb: 2, scrollbarWidth: 'thin' }}>
        {groups.map((group) => (
          <Box key={group.title} sx={{ mt: 1.5 }}>
            {!collapsed && (
              <Typography
                variant="overline"
                color="text.disabled"
                sx={{ px: 1.5, display: 'block', mb: 0.5 }}
              >
                {group.title}
              </Typography>
            )}
            {group.items.map((item) => (
              <NavButton
                key={item.path}
                label={item.label}
                path={item.path}
                icon={item.icon}
                collapsed={collapsed}
                badge={item.path === '/support' ? (supportUnread ?? 0) : 0}
              />
            ))}
          </Box>
        ))}
      </Box>

      {!collapsed && (
        <PoweredBy sx={{ py: 1.25, borderTop: 1, borderColor: 'divider', flexShrink: 0 }} />
      )}
      {showToggle && collapsed && (
        <Box sx={{ p: 1.5, display: 'flex', justifyContent: 'center' }}>
          <Tooltip title="Expand sidebar" placement="right">
            <IconButton
              size="small"
              onClick={onToggle}
              sx={{ color: 'text.secondary', transform: 'rotate(180deg)' }}
            >
              <KeyboardDoubleArrowLeftRounded fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>
      )}
    </Box>
  );
}

function NavButton({
  label,
  path,
  icon: Icon,
  collapsed,
  badge = 0,
}: {
  label: string;
  path: string;
  icon: typeof MenuRounded;
  collapsed: boolean;
  /** Unread count shown next to the label (e.g. support replies). */
  badge?: number;
}) {
  const button = (
    <ButtonBase
      component={NavLink}
      to={path}
      end={path === '/'}
      sx={(t) => {
        const primary = t.vars?.palette.primary.main ?? t.palette.primary.main;
        return {
          width: '100%',
          justifyContent: collapsed ? 'center' : 'flex-start',
          gap: 1.5,
          px: collapsed ? 0 : 1.5,
          height: 40,
          mb: 0.25,
          borderRadius: '10px',
          color: 'text.secondary',
          fontSize: '0.875rem',
          fontWeight: 550,
          position: 'relative',
          transition: 'background-color 140ms, color 140ms',
          '&:hover': { bgcolor: 'action.hover', color: 'text.primary' },
          '&.active': {
            color: 'primary.main',
            bgcolor: `color-mix(in srgb, ${primary} 10%, transparent)`,
            fontWeight: 650,
            '&::before': {
              content: '""',
              position: 'absolute',
              left: -12,
              top: 10,
              bottom: 10,
              width: 3,
              borderRadius: 3,
              bgcolor: 'primary.main',
            },
          },
        };
      }}
    >
      <Badge badgeContent={collapsed ? badge : 0} color="error" variant="dot" overlap="circular">
        <Icon sx={{ fontSize: 20 }} />
      </Badge>
      {!collapsed && (
        <>
          <Box component="span" sx={{ flex: 1, textAlign: 'left' }}>
            {label}
          </Box>
          {badge > 0 && (
            <Badge
              badgeContent={badge}
              color="error"
              max={99}
              aria-label={`${String(badge)} unread`}
              sx={{ mr: 1.25 }}
            />
          )}
          {!hasScreen(path) && !FULL_SCREEN_PATHS.has(path) && (
            <Box
              component="span"
              sx={{
                fontSize: 10,
                fontWeight: 700,
                color: 'text.disabled',
                letterSpacing: '0.04em',
              }}
            >
              SOON
            </Box>
          )}
        </>
      )}
    </ButtonBase>
  );
  return collapsed ? (
    <Tooltip title={label} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
}

/** The app mark, or the shop's logo when one is uploaded (`logoUrl`). */
export function BrandMark({ size = 36, logoUrl }: { size?: number; logoUrl?: string | null }) {
  const { src } = useImageSrc(logoUrl);
  if (src)
    return (
      <Box
        component="img"
        src={src}
        alt=""
        sx={{
          width: size,
          height: size,
          flexShrink: 0,
          objectFit: 'contain',
          borderRadius: `${String(Math.round(size * 0.3))}px`,
          bgcolor: '#fff',
          border: 1,
          borderColor: 'divider',
        }}
      />
    );
  return (
    <Box
      sx={(t) => {
        const p = t.vars?.palette.primary ?? t.palette.primary;
        return {
          width: size,
          height: size,
          borderRadius: `${String(Math.round(size * 0.3))}px`,
          flexShrink: 0,
          display: 'grid',
          placeItems: 'center',
          color: '#fff',
          background: `linear-gradient(135deg, ${p.light}, ${p.main} 55%, ${p.dark})`,
          boxShadow: `0 6px 14px -6px ${p.main}, inset 0 1px 0 rgba(255,255,255,0.25)`,
        };
      }}
    >
      <svg width={size * 0.55} height={size * 0.55} viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M4 9.5 5.6 4.8A1.2 1.2 0 0 1 6.7 4h10.6a1.2 1.2 0 0 1 1.1.8L20 9.5"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M4 9.5a2.67 2.67 0 0 0 5.33 0 2.67 2.67 0 0 0 5.34 0 2.67 2.67 0 0 0 5.33 0"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
        />
        <path
          d="M5.5 12.5V19a1 1 0 0 0 1 1h11a1 1 0 0 0 1-1v-6.5M10 20v-4.5h4V20"
          stroke="currentColor"
          strokeWidth="1.8"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </Box>
  );
}

function TopBar({
  onMenu,
  showMenu,
  onSearch,
}: {
  onMenu: () => void;
  showMenu: boolean;
  onSearch: () => void;
}) {
  const { me } = useAuth();
  const isMac = typeof navigator !== 'undefined' && /mac/i.test(navigator.userAgent);

  return (
    <Box
      component="header"
      sx={(t) => ({
        position: 'sticky',
        top: 0,
        zIndex: t.zIndex.appBar,
        height: 68,
        display: 'flex',
        alignItems: 'center',
        gap: { xs: 0.5, sm: 1.5 },
        px: { xs: 1.5, sm: 3, lg: 4 },
        backdropFilter: 'saturate(180%) blur(14px)',
        backgroundColor: alpha(t.palette.background.default, 0.78),
        ...t.applyStyles('dark', { backgroundColor: alpha('#0A0F1A', 0.72) }),
        borderBottom: `1px solid ${t.vars?.palette.divider ?? t.palette.divider}`,
      })}
    >
      {showMenu && (
        <IconButton onClick={onMenu} edge="start" aria-label="Open navigation">
          <MenuRounded />
        </IconButton>
      )}
      <BackButton />
      <ButtonBase
        onClick={onSearch}
        sx={(t) => ({
          flex: 1,
          minWidth: 0,
          maxWidth: 440,
          height: 40,
          px: 1.5,
          gap: 1,
          justifyContent: 'flex-start',
          borderRadius: '10px',
          color: 'text.secondary',
          bgcolor: 'background.paper',
          border: `1px solid ${t.vars?.palette.divider ?? t.palette.divider}`,
          boxShadow: '0 1px 2px rgba(16,24,40,0.04)',
          fontSize: '0.875rem',
          transition: 'border-color 140ms',
          '&:hover': { borderColor: 'grey.300' },
        })}
      >
        <SearchRounded sx={{ fontSize: 20 }} />
        <Box
          component="span"
          sx={{
            flex: 1,
            textAlign: 'left',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          Search or jump to…
        </Box>
        <Box
          component="kbd"
          sx={{
            display: { xs: 'none', sm: 'inline-flex' },
            fontFamily: 'inherit',
            fontSize: 11,
            fontWeight: 600,
            px: 0.75,
            py: 0.25,
            borderRadius: '6px',
            border: 1,
            borderColor: 'divider',
            color: 'text.secondary',
          }}
        >
          {isMac ? '⌘K' : 'Ctrl K'}
        </Box>
      </ButtonBase>
      <Box sx={{ flex: { xs: 0, sm: 1 } }} />
      {/* Filled by the desktop app (its sync status); empty on the web. */}
      <Box id="app-header-slot" sx={{ display: 'contents' }} />
      <ModeToggle />
      <NotificationsBell />
      {me && <UserMenu />}
    </Box>
  );
}

export function ModeToggle() {
  const { mode, setMode } = useColorScheme();
  const dark = mode === 'dark';
  return (
    <Tooltip title={dark ? 'Switch to light mode' : 'Switch to dark mode'}>
      <IconButton
        onClick={() => setMode(dark ? 'light' : 'dark')}
        aria-label="Toggle dark mode"
        sx={{ color: 'text.secondary' }}
      >
        {dark ? <LightModeRounded fontSize="small" /> : <DarkModeRounded fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
}

function NotificationsBell() {
  const navigate = useNavigate();
  const { data } = useQuery({
    queryKey: ['notifications', 'unread-count'],
    queryFn: () => unwrap(api.GET('/notifications/unread-count')),
    refetchInterval: 60_000,
  });
  const count = data?.unreadCount ?? 0;
  return (
    <Tooltip title="Notifications">
      <IconButton
        aria-label="Notifications"
        onClick={() => void navigate('/notifications')}
        sx={{ color: 'text.secondary' }}
      >
        <Badge badgeContent={count} color="error" max={99} overlap="circular">
          <NotificationsNoneRounded fontSize="small" />
        </Badge>
      </IconButton>
    </Tooltip>
  );
}

function UserMenu() {
  const { me, logout } = useAuth();
  const navigate = useNavigate();
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const { src } = useImageSrc(me?.user.imageUrl);
  if (!me) return null;
  const role = me.isOwner ? 'Owner' : (me.roles[0]?.name ?? 'Staff');
  return (
    <>
      <ButtonBase
        onClick={(e) => setAnchor(e.currentTarget)}
        aria-label={`Account menu: ${me.user.name}, ${role}`}
        aria-haspopup="menu"
        sx={{ borderRadius: '12px', p: 0.5, pr: { xs: 0.5, md: 1.25 }, gap: 1.25, flexShrink: 0 }}
      >
        <Avatar
          src={src ?? undefined}
          sx={{ width: 34, height: 34, bgcolor: 'primary.main', fontSize: 13 }}
        >
          {initials(me.user.name)}
        </Avatar>
        <Box sx={{ display: { xs: 'none', md: 'block' }, textAlign: 'left' }}>
          <Typography variant="subtitle2" sx={{ lineHeight: 1.2 }}>
            {me.user.name}
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {role}
          </Typography>
        </Box>
      </ButtonBase>
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { sx: { minWidth: 240 } } }}
      >
        <Stack direction="row" spacing={1.5} sx={{ px: 1.5, py: 1, alignItems: 'center' }}>
          <ImageEditor target={{ kind: 'me' }} url={me.user.imageUrl} canEdit label="you">
            <Avatar
              src={src ?? undefined}
              sx={{ width: 48, height: 48, bgcolor: 'primary.main', fontSize: 17 }}
            >
              {initials(me.user.name)}
            </Avatar>
          </ImageEditor>
          <Box sx={{ minWidth: 0 }}>
            <Typography variant="subtitle2">{me.user.name}</Typography>
            <Typography variant="caption" color="text.secondary">
              {me.user.email ?? me.user.phone}
            </Typography>
            <Box sx={{ mt: 0.5 }}>
              <Chip size="small" label={role} color="primary" variant="outlined" />
            </Box>
          </Box>
        </Stack>
        <Divider sx={{ my: 0.5 }} />
        <MenuItem onClick={() => setAnchor(null)}>
          <ListItemIcon>
            <PersonOutlineRounded fontSize="small" />
          </ListItemIcon>
          My profile
        </MenuItem>
        <MenuItem
          onClick={() => {
            setAnchor(null);
            void logout().then(() => navigate('/login'));
          }}
        >
          <ListItemIcon>
            <LogoutRounded fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
